import * as React from 'react'
import { FormikProps } from 'formik'
import { FormikTextField } from 'components'
import { Autocomplete, Checkbox, Grid, useTheme } from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import {
  CustomerDevice,
  CustomerUser,
  IdKeyedObjectShape,
  User,
} from 'contexts/base'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

interface EditDeviceFormProps {
  formikProps: FormikProps<CustomerDevice>
  users: CustomerUser[]
  readOnly: boolean
  agencyUsersLookup: IdKeyedObjectShape<User>
  allowSendingActivationPin: boolean
}

const noAssignedUserOption: Option = {
  key: '',
  value: '',
  label: '<No Assigned User>',
}

const getUserLabel = (user: CustomerUser) => {
  return `${user.fullName} (${user.name})`
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sendActivationCode: {
      marginBottom: theme.spacing(8),
    }
  }),
)

export const EditDeviceForm: React.FunctionComponent<EditDeviceFormProps> = ({
  formikProps,
  users,
  readOnly,
  agencyUsersLookup,
  allowSendingActivationPin,
}) => {
  const theme = useTheme<Theme>()
  const styles = useStyles(theme);

  const [userOptions, setUserOptions] = React.useState<Option[]>([
    noAssignedUserOption,
    ...users
      .map((user) => ({
        ...user,
        isActive: user.isActive,
      }))
      .filter(
        (user) =>
          user.isActive || formikProps.values.assignedUserId === user.id,
      )
      .map((user) => ({
        key: user.id,
        value: user.id,
        label: user.isActive
          ? getUserLabel(user)
          : `${getUserLabel(user)} (inactive)`,
      })),
  ])

  React.useEffect(() => {
    setUserOptions([
      noAssignedUserOption,
      ...users
        .map((user) => ({
          ...user,
          isActive: user.isActive,
        }))
        .filter(
          (user) =>
            user.isActive || formikProps.values.assignedUserId === user.id,
        )
        .map((user) => ({
          key: user.id,
          value: user.id,
          label: user.isActive
            ? getUserLabel(user)
            : `${getUserLabel(user)} (inactive)`,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1)),
    ])
  }, [users, agencyUsersLookup])

  const canSendActivationPin = !!formikProps.values.assignedUserId;
  React.useEffect(() => {
    formikProps.setFieldValue('sendPinToAssignedUser', false)
  }, [canSendActivationPin])

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <FormikTextField
          formikProps={formikProps}
          fieldName="name"
          label="Name"
          fullWidth
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          id="assignedUserId"
          freeSolo={false}
          options={userOptions}
          getOptionLabel={(option) => option.label}
          value={userOptions.find(
            (p) => p.key == formikProps.values.assignedUserId,
          )}
          onChange={(event, newValue) => {
            formikProps.setFieldValue('assignedUserId', newValue?.key)
          }}
          onBlur={formikProps.handleBlur}
          renderInput={(params) => (
            <FormikTextField
              {...params}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...theme.components?.MuiTextField?.defaultProps?.InputLabelProps,
              }}
              label="Assigned User"
              formikProps={formikProps}
              fieldName="assignedUserId"
              fullWidth
            />
          )}
        />
      </Grid>
      {allowSendingActivationPin &&
        <Grid item xs={12} className={styles.sendActivationCode}>
          <Checkbox
            label='Text Activation Key'
            checked={!!formikProps.values.sendPinToAssignedUser}
            onChange={(_, newValue) => formikProps.setFieldValue('sendPinToAssignedUser', newValue)}
            disabled={!canSendActivationPin} />
        </Grid>
      }
      <Grid item xs={12}>
        <FormikTextField
          formikProps={formikProps}
          fieldName="notes"
          label="Notes"
          fullWidth
          multiline
          rows={5}
          readOnly={readOnly}
        />
      </Grid>
    </React.Fragment>
  )
}
